<template>
  <div class="content-page">
    <div>
      <TheBreadcrumb :pages="arianeRoute" />
      <NuxtLazyHydrate when-idle>
        <HeaderContent
          v-if="!source"
          :page="currentPageInfo"
        />
        <HeaderSource
          v-else
          :page="currentPageInfo"
        />
      </NuxtLazyHydrate>
      <div class="post-container">
        <div
          v-if="
            currentPageInfo &&
            'favicon' in currentPageInfo &&
            currentPageInfo.favicon &&
            source
          "
          class="favicon"
        >
          <img
            :src="currentPageInfo.favicon.url"
            :alt="`favicon de ${currentPageInfo.title}`"
          />
        </div>
        <PostItem
          :source="source"
          :side-content="sideContent"
          :post-content="postContent"
        />
      </div>
      <BlueSection
        :connected="authenticated"
        diagonal="none"
        class="blue-section-item"
        :challenges="monthlyChallenges"
      />
      <DoNotMissSection
        v-if="doNotMiss.length"
        :slider-items="doNotMiss"
      />
      <OfferSection
        v-if="offer"
        :offer="offer"
        >Acheter ce jeu</OfferSection
      >
      <section
        v-if="otherCommunities.length"
        class="other-communities section"
      >
        <div class="title-line line">
          <h2 class="title-strat">ces communautés peuvent vous intéresser</h2>
          <NuxtLink
            to="/communautes"
            class="button-more"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 19.328 34.43"
            >
              <path
                d="M18.71 15.72 3.66.62a2.178 2.178 0 0 0-3.041 0 2.178 2.178 0 0 0 0 3.041l13.555 13.555L.619 30.77a2.178 2.178 0 0 0 0 3.041 2.178 2.178 0 0 0 3.041 0l15.05-15.05a2.178 2.178 0 0 0 0-3.041Z"
                fill="#fff"
                fill-rule="evenodd"
              />
            </svg>
            Plus de communautés
          </NuxtLink>
        </div>
        <div class="slider">
          <CommunitySlider :communities="otherCommunities" />
        </div>
      </section>
      <p class="mention">
        * Récompense provisoire sous réserve de maintenir votre place au
        classement jusqu'à la fin du mois.
      </p>
    </div>
    <PegiWarning v-if="media?.community?.pegi === '18'" />
  </div>
</template>

<script setup lang="ts">
import type { Category4 } from '~/types'

definePageMeta({
  middleware: async (to) => {
    const loadMediaReviewPostTitle = useLoadMediaReviewPostTitle()
    const redirectMovedPost = useRedirectMovedPost()
    return (await loadMediaReviewPostTitle(to)) ?? redirectMovedPost(to)
  },
})

const challengesStore = useChallengesStore()
const { monthlyChallenges } = storeToRefs(challengesStore)

const slidersStore = useDoNotMissStore()
const { doNotMiss } = storeToRefs(slidersStore)

const mediasStore = useMediasStore()
const { media, otherMedias } = storeToRefs(mediasStore)

const arianeRoute = computed(() => {
  return [
    {
      title: 'Toutes les communautés',
      route: '/communautes',
    },
    {
      title: media.value?.community?.title,
      route: `/communautes/${media.value?.community?.slug}`,
    },
    {
      title: media.value?.category.name,
      route: `/communautes/${media.value?.community?.slug}/${media.value?.category.slug}`,
    },
    {
      title: media.value?.title,
      route: ``,
    },
  ]
})

const source = ref(media.value?.type === 'source')

const newsArticleSchema = useNewsArticleSchema({
  post: media.value,
  source,
})

const breadcrumbSchema = useBreadcrumbSchema({
  arianeRoute,
})

useHead({
  title: media.value?.title_seo,
  script: [newsArticleSchema, breadcrumbSchema],
})

useSeoMeta({
  // posts titled with a pasted URL or web search (Post::isSeoThin): kept out of Google
  robots: media.value?.seo_noindex ? 'noindex, follow' : undefined,
  title: media.value?.title_seo,
  description: media.value?.description_seo,
  keywords: media.value?.keywords_seo,
  ogTitle: media.value?.title_og,
  ogType: 'article',
  articlePublishedTime: media.value?.created_at,
  articleModifiedTime: media.value?.updated_at || media.value?.created_at,
  articleAuthor: media.value?.user?.nickname
    ? [media.value.user.nickname]
    : undefined,
  ogDescription: media.value?.description_og,
  ogImage:
    media.value?.image_og?.url ??
    'https://www.weareplaystation.fr/img/og-img.jpg',
})

const currentPageInfo = computed(() => {
  const rawMedia = unref(media)

  const data = {
    title: rawMedia?.title,
    contentType: rawMedia?.type,
    type: rawMedia?.category.name,
    pegi: rawMedia?.community?.pegi,
    image: rawMedia?.image?.url,
    imageLegend: rawMedia?.image_legend,
  }

  if (rawMedia?.type === 'source') {
    return {
      ...data,
      favicon: rawMedia?.favicon,
      urlVideo: rawMedia?.embed,
    }
  } else {
    return {
      ...data,
      subject: rawMedia?.community?.title,
      content: rawMedia?.content,
      urlVideo: rawMedia?.video,
    }
  }
})

const route = useRoute()

if (
  media.value?.category?.slug !== 'avis-medias' ||
  media.value?.community?.slug !== route.params.communityName
) {
  throw createError({ statusCode: usePostGoneState().value ? 410 : 404 })
}

const slug = computed(() => {
  return route.params.postTitle as string
})

const socialStore = useSocialStore()
const { social } = storeToRefs(socialStore)

const popoutStore = usePopoutStore()
const { popout } = popoutStore

const postContent = computed(() => {
  const rawMedia = unref(media)
  const rawSocial = unref(social)

  return {
    firstTag: {
      title: rawMedia?.community?.title,
      slug: rawMedia?.community?.slug,
    },
    type: rawMedia?.category.name,
    createdAt: rawMedia?.created_at,
    updatedAt: rawMedia?.updated_at,
    sourceAuthor: rawMedia?.source_author,
    sourceAuthorUrl: rawMedia?.source_author_url,
    description: rawMedia?.description,
    slugType: rawMedia?.category.slug,
    pegi: rawMedia?.community?.pegi,
    url: rawMedia?.link,
    title: rawMedia?.title,
    user: rawMedia?.user,
    followed: rawSocial.is_following,
    content: rawMedia?.content,
    contentImages: rawMedia?.content_images,
    contentEmbed: rawMedia?.content_embed,
    tags: rawMedia?.tags,
    likes: rawSocial.likes_count,
    shares: rawSocial.shares_count,
    comments: rawSocial.comments,
    commentsCount: rawSocial.comments_count,
    has_more_comments: rawSocial.has_more_comments,
    is_liked: rawSocial.is_liked,
    slug: slug.value,
    urlVideo: rawMedia?.video,
    popout,
  }
})

const { offer, contentBlocksType } = useOffer(media)

const sideContent = computed(() => {
  const navSide: Category4[] = [{ name: 'La communauté', slug: '' }]

  contentBlocksType.value?.forEach((element) => {
    if (element?.category) {
      const categorySlug = element.category.slug
      if (
        categorySlug === 'actualites-communaute' ||
        categorySlug === 'avis-joueurs' ||
        categorySlug === 'avis-medias' ||
        categorySlug === 'le-coin-des-wapers' ||
        categorySlug === 'astuces'
      ) {
        navSide.push(element.category)
      }
    } else if (element?.slug === 'wapers') {
      navSide.push({ name: 'Les Wapers influents', slug: 'wapers' })
    }
  })

  return {
    title: media.value?.community?.title,
    navName: 'avis de médias',
    type: media.value?.category.name,
    route: `/communautes/${media.value?.community?.slug}`,
    nav: navSide,
    cards: otherMedias.value,
    subCategoriesFilters: false,
  }
})

const otherCommunities = computed(() => {
  const communityBlock = contentBlocksType.value?.find(
    (block) => block?.slug === 'communautes',
  )

  if (!communityBlock?.communities) return []

  return communityBlock.communities.map((item) => ({
    ...item,
    route: `/communautes/${item.slug}`,
  }))
})

watch(media, (newValue) => {
  if (newValue?.category.slug !== 'avis-medias') {
    throw createError({ statusCode: 404 })
  }
})

const { authenticated } = useAuthenticated()
const loadMediaReviewPostTitle = useLoadMediaReviewPostTitle()

watch(authenticated, (newValue) => {
  if (newValue) {
    loadMediaReviewPostTitle(route)
  }
})
</script>

<style lang="scss" scoped>
.content-page {
  .post-container {
    position: relative;

    .favicon {
      position: absolute;
      top: -4.4rem;
      left: 8rem;
      z-index: 2;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 8.4rem;
      height: 8.4rem;
      background-color: $white;
      border-radius: 4.2rem;

      img {
        width: 5.6rem;
        height: 3.8rem;
        object-fit: contain;
      }

      @media screen and (max-width: 430px) {
        left: 2.5rem;
        width: 7rem;
        height: 7rem;
        border-radius: 3.5rem;
      }
    }
  }

  .mention {
    margin-top: 10rem;
  }
}

@media screen and (max-width: 700px) {
  .content-page {
    .other-communities {
      padding: 0;
    }

    .mention {
      margin: 2.5em;
    }
  }
}
</style>
